import { config} from '../../config';
import RequestError from '../../RequestError';
import { RegisterObject } from '../../types/model';
import { getInfoFromJwt, getAuthToken, formatData } from './utils';
import { LIVE_BROADCAST_ALLOWED } from './liveBroadcastAllowlist';

type FetchConfigType = {
  headers: {
    [key: string]: any;
  };
};

const REQUEST_ORIGIN = 'REQUEST-ORIGIN';

function isLiveBroadcastAllowed(country?: string, language?: string) {
  if (!country || !language) {
    return false;
  }
  const allowedLanguages = LIVE_BROADCAST_ALLOWED[country.toLowerCase()];
  if (!allowedLanguages) {
    return false;
  }
  return allowedLanguages.includes(language.toLowerCase());
}

function getFetchConfig() {
  const fetchConfig: FetchConfigType = { headers: {} };
  fetchConfig.headers[REQUEST_ORIGIN] = config.requestOrigin;

  const bearerToken = getAuthToken();
  if (bearerToken) {
    fetchConfig.headers['X-USER-AUTH'] = `Bearer ${bearerToken}`;
  }

  return fetchConfig;
}

function getPutFetchConfig(settings: { [key: string]: any } = {}) {
  const fetchConfig = getFetchConfig();
  fetchConfig.headers['Content-Type'] = 'application/json';
  return { ...fetchConfig, ...settings };
}

export async function getStores() {
  const country = config.storeMarketOverride || config.country;
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/stores?countryCode=${country}`,
    getFetchConfig()
  );

  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }

  const stores = await res.json();

  return stores;
}

export async function getEvents(storeNo?: string) {
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/events?countryCode=${config.country}&storeNo=${storeNo}&status=ACTIVE`,
    getFetchConfig()
  );
  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }
  const events = await res.json();
  return events;
}

//get all Events from a country in sorted order by their closest upcoming date
export async function getAllEventsFromCountry() {
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/events?countryCode=${config.country}&limit=${config.carouselEventLimits}&status=ACTIVE`,
    getFetchConfig()
  );

  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }

  const events = await res.json();
  return events;
}

export async function getEventHead(eventId: string) {
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/events/status/${eventId}`,
    { method: 'HEAD' }
  );
  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }
  return res;
}

export async function getEvent(eventId: string) {
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/events/${eventId}`,
    getFetchConfig()
  );
  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }
  return res.json();
}

// QR Self-Checkin
export async function putRegistration(
  eventId: string,
  timeSlotId: string,
  registration: any
) {
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/events/${eventId}/timeslots/${timeSlotId}/registrations`,
    getPutFetchConfig({ method: 'PUT', body: JSON.stringify(registration) })
  );

  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }
}

export async function registerConsumer(
  eventId: string,
  timeSlotId: string,
  data: RegisterObject
) {
  const fetchConfig = getFetchConfig();

  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/events/${eventId}/timeslots/${timeSlotId}/registrations`,
    {
      ...fetchConfig,
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        ...fetchConfig.headers,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }
}

export async function authenticate() {
  const { storeEventApiHost, userInfoApiAuthHeaderICM } = config;
  const bearerToken = getAuthToken();
  const { loyaltyPrograms } = getInfoFromJwt(bearerToken);
  let headers = { [config.userInfoApiAuthHeader]: `Bearer ${bearerToken}` };
  if (config.userInfoApiClientId) {
    headers['x-client-id'] = config.userInfoApiClientId;
  }

  const resp = await fetch(`${storeEventApiHost}/api/v1.0/events/customer`, {
    headers: {
      [userInfoApiAuthHeaderICM]: `Bearer ${bearerToken}`,
      [REQUEST_ORIGIN]: config.requestOrigin,
    },
  });

  const data = await resp.json();

  if (data) {
    data.loyaltyPrograms = loyaltyPrograms;
  }

  return formatData(data);
}
// Get all live broadcast events from a country if the country/language combination is allowed,
// otherwise returns an empty array, in sorted order by their closest upcoming date.
export async function getAllLiveBroadcast() {
  if (!isLiveBroadcastAllowed(config.country, config.language)) {
    // Intentionally return an empty array when live broadcasts are not allowed
    // for the current country/language combination.
    return [];
  }
  const res = await fetch(
    `${config.storeEventApiHost}/api/v1.0/liveshows/${config.country}/${config.language}`,
    getFetchConfig()
  );

  if (!res.ok) {
    throw new RequestError(res.status, res.statusText);
  }

  const events = await res.json();
  return events;
}